<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="saveTemplate">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('Template bearbeiten') : $t('Template anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit">
                                <span>{{ $t('Save') }}</span>
                                <span
                                    class="h-2 w-2 flex-shrink-0 rounded-full"
                                    :class="[publicationStateDotClass, showPublicationStateDot ? '' : 'opacity-0']"
                                    :title="publicationStateLabel"
                                    :aria-label="publicationStateLabel"
                                ></span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('publish')">
                                    {{ $t('Veröffentlichen') }}
                                </button>
                                <button v-if="template.published_at" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('reset')">
                                    {{ $t('Zurücksetzen (letzte Veröffentlichung)') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('preview')">
                                    {{ $t('Vorschau') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button v-if="template.is_system === false" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('delete')">
                                    {{ $t('Template löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit">
                            {{ $t('Template anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="space-y-6">
                    <app-panel :title="$t('Allgemein')">
                            <div class="grid grid-cols-1 gap-6">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                    <input v-model="template.name" class="stage-filter-input form-input block w-full text-sm" required>
                                </div>
                                <div class="w-full">
                                    <label class="stage-filter-label block mb-2">{{ $t('Beschreibung') }}</label>
                                    <textarea v-model="template.description" class="stage-filter-input form-textarea block w-full text-sm" rows="3"></textarea>
                                </div>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('HTML (Sprachvarianten)')" body-class="!p-0">
                            <div v-if="template.translations.length > 0" class="flex flex-col">
                                <div class="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5">
                                    <div class="flex items-center">
                                        <button
                                            v-for="(translation, index) in template.translations"
                                            :key="translation.id || ('new-' + index)"
                                            type="button"
                                            class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                            :class="activeTranslationIndex === index ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                            @click="activeTranslationIndex = index"
                                        >
                                            {{ translationLabel(translation, index) }}
                                        </button>
                                    </div>
                                    <button
                                        class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700"
                                        type="button"
                                        :title="$t('Datei einfügen')"
                                        :aria-label="$t('Datei einfügen')"
                                        @click="openTemplateHtmlMediaDrawer"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.image"></svg-vue>
                                    </button>
                                </div>

                                <div v-if="activeTranslation" class="min-h-0 p-0">
                                    <div class="cms-translation-html-layout">
                                        <div class="h-full min-h-0 overflow-hidden !p-0">
                                            <cms-html-code-editor ref="templateHtmlEditor" v-model="activeTranslation.layout_html" :rows="24" variant="vault" fill />
                                        </div>
                                        <div class="cms-variable-column">
                                            <div class="cms-variable-column-body">
                                                <div class="max-h-[34rem] overflow-y-auto">
                                                    <div
                                                        v-for="variable in htmlVariables"
                                                        :key="variable.key"
                                                        class="cms-variable-row text-xs"
                                                    >
                                                        <div class="font-mono text-gray-900">{{ variable.key }}</div>
                                                        <div class="text-gray-500 mt-1">{{ variable.description }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else class="p-6 text-sm text-gray-500">
                                {{ $t('Noch keine Sprachvarianten vorhanden.') }}
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Code')" panel-class="self-start" body-class="!p-0 h-[34rem] overflow-hidden">
                            <div class="flex h-full flex-col">
                                <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 bg-white px-5">
                                    <button
                                        v-for="tab in codeTabs"
                                        :key="tab.key"
                                        type="button"
                                        class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                        :class="activeCodeTab === tab.key ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                        @click="activeCodeTab = tab.key"
                                    >
                                        {{ tab.label }}
                                    </button>
                                </div>

                                <div v-show="activeCodeTab === 'layout_css'" class="h-full min-h-0 flex-1">
                                    <html-code-editor v-model="template.layout_css" :rows="24" variant="vault" fill />
                                </div>
                                <div v-show="activeCodeTab === 'layout_js_head'" class="h-full min-h-0 flex-1">
                                    <html-code-editor v-model="template.layout_js_head" :rows="24" variant="vault" fill />
                                </div>
                                <div v-show="activeCodeTab === 'layout_js_footer'" class="h-full min-h-0 flex-1">
                                    <html-code-editor v-model="template.layout_js_footer" :rows="24" variant="vault" fill />
                                </div>
                            </div>
                    </app-panel>

                </div>
            </div>
        </form>
        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-template-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </main>
</template>

<script>
import HtmlCodeEditor from "@/components/forms/html-code-editor";
import CmsHtmlCodeEditor from "@/components/forms/cms-html-code-editor";
import ConfirmDialog from "@/components/modals/confirm-dialog";

export default {
    name: "cms-template-editor",
    components: {
        HtmlCodeEditor,
        CmsHtmlCodeEditor,
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            headerActionMenuOpen: false,
            templateStateResolved: false,
            activeTranslationIndex: 0,
            activeCodeTab: 'layout_css',
            languageOptions: [],
            template: {
                key: '',
                name: '',
                description: '',
                layout_css: '',
                layout_js_head: '',
                layout_js_footer: '',
                is_system: null,
                has_unpublished_changes: false,
                published_at: null,
                translations: [],
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        codeTabs() {
            return [
                {key: 'layout_css', label: 'CSS'},
                {key: 'layout_js_head', label: 'JS (Head)'},
                {key: 'layout_js_footer', label: 'JS (Footer)'},
            ];
        },
        activeTranslation() {
            return this.template.translations[this.activeTranslationIndex] || null;
        },
        htmlVariables() {
            return [
                {key: '{{ page_title }}', description: this.$t('Seitentitel')},
                {key: '{{ page_name }}', description: this.$t('Interner Seitenname')},
                {key: '{{ page_key }}', description: this.$t('Eindeutiger Seitenschlüssel')},
                {key: '{{ page_locale }}', description: this.$t('Sprachcode der Seite')},
                {key: '{{ app_name }}', description: this.$t('Anwendungsname')},
                {key: '{{ base_url }}', description: this.$t('Basis-URL des Systems')},
                {key: '{{ cms_css_url }}', description: this.$t('Generierte CMS-CSS URL')},
                {key: '{{ content_html }}', description: this.$t('Gerenderter Seiteninhalt')},
                {key: '{{ template_css }}', description: this.$t('Template-CSS Block')},
                {key: '{{ page_css }}', description: this.$t('Seiten-CSS Block')},
                {key: '{{ template_js_head }}', description: this.$t('Template-JS für den Head-Bereich')},
                {key: '{{ template_js_footer }}', description: this.$t('Template-JS am Seitenende')},
                {key: '{{ page_js_footer }}', description: this.$t('Seiten-JS am Seitenende')},
            ];
        },
        publicationStateLabel() {
            if (!this.template.published_at) {
                return this.$t('Noch nicht veröffentlicht');
            }

            if (this.template.has_unpublished_changes) {
                return this.$t('Unveröffentlichte Änderungen');
            }

            return this.$t('Veröffentlicht');
        },
        publicationStateClass() {
            if (!this.template.published_at) {
                return 'text-gray-500';
            }

            return this.template.has_unpublished_changes ? 'text-amber-600' : 'text-green-600';
        },
        publicationStateDotClass() {
            if (!this.template.published_at) {
                return 'bg-amber-500';
            }

            return this.template.has_unpublished_changes ? 'bg-amber-500' : 'bg-green-500';
        },
        showPublicationStateDot() {
            return !this.isEdit || this.templateStateResolved;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        this.loadOptions().finally(() => {
            if (this.isEdit) {
                this.getTemplate();
            } else {
                this.syncTranslations();
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'publish') {
                this.publishTemplate();
                return;
            }

            if (action === 'reset') {
                this.resetTemplateDraft();
                return;
            }

            if (action === 'preview') {
                this.previewTemplate();
                return;
            }

            if (action === 'cancel') {
                this.$router.push('/workspace/cms/templates');
                return;
            }

            if (action === 'delete') {
                this.deleteTemplate();
            }
        },
        handleDocumentClick(event) {
            if (!this.headerActionMenuOpen) {
                return;
            }

            const menuElement = this.$refs.headerActionMenu;
            const triggerElement = this.$refs.headerActionMenuTrigger;
            const wrapperElement = this.$refs.headerActionMenuWrapper;

            if (!(menuElement && menuElement.contains(event.target))
                && !(triggerElement && triggerElement.contains(event.target))
                && !(wrapperElement && wrapperElement.contains(event.target))) {
                this.closeHeaderActionMenu();
            }
        },
        loadOptions() {
            return axios.get('api/workspace/admin/cms/templates/options').then((response) => {
                this.languageOptions = Array.isArray(response.data.languages) ? response.data.languages : [];
                this.syncTranslations();
            });
        },
        getTemplate() {
            this.templateStateResolved = false;
            this.loading = true;
            axios.get('api/workspace/admin/cms/templates/' + this.$route.params.id).then((response) => {
                this.template = response.data;
                if (!Array.isArray(this.template.translations)) {
                    this.template.translations = [];
                }
                this.syncTranslations();
                this.activeTranslationIndex = 0;
                this.templateStateResolved = true;
                this.loading = false;
            }).catch(() => {
                this.templateStateResolved = true;
                this.loading = false;
            });
        },
        saveTemplate() {
            return this.persistTemplate();
        },
        publishTemplate() {
            this.persistTemplate(true);
        },
        resetTemplateDraft() {
            this.openConfirmDialog({
                title: this.$t('Auf veröffentlichte Version zurücksetzen'),
                text: this.$t('Soll der aktuelle Entwurf wirklich auf die zuletzt veröffentlichte Version zurückgesetzt werden? Nicht veröffentlichte Änderungen gehen dabei verloren.'),
                pendingAction: () => {
                    axios.post('api/workspace/admin/cms/templates/' + this.$route.params.id + '/reset').then((response) => {
                        this.template = response.data.template;
                        this.syncTranslations();
                        this.$notify({title: this.$t('Success'), text: this.$t('Entwurf auf veröffentlichte Version zurückgesetzt'), type: 'success'});
                    });
                },
            });
        },
        previewTemplate() {
            const previewWindow = window.open('about:blank', '_blank', 'noopener');
            this.openPreview(previewWindow);
        },
        deleteTemplate() {
            this.openConfirmDialog({
                title: this.$t('Template löschen'),
                text: this.$t('Soll dieses Template wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/templates/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push('/workspace/cms/templates');
                    });
                },
            });
        },
        persistTemplate(publishAfterSave = false) {
            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/templates/' + this.$route.params.id, this.template)
                : axios.post('api/workspace/admin/cms/templates', this.template);

            return request.then((response) => {
                const templateId = response.data.template.id;

                if (!this.isEdit) {
                    this.$router.push('/workspace/cms/templates/' + templateId + '/edit');
                }

                this.template = response.data.template;
                this.syncTranslations();
                this.activeTranslationIndex = Math.min(this.activeTranslationIndex, Math.max(this.template.translations.length - 1, 0));
                this.templateStateResolved = true;

                if (publishAfterSave) {
                    return axios.post('api/workspace/admin/cms/templates/' + templateId + '/publish').then((publishResponse) => {
                        this.template = publishResponse.data.template;
                        this.syncTranslations();
                        this.activeTranslationIndex = Math.min(this.activeTranslationIndex, Math.max(this.template.translations.length - 1, 0));
                        this.templateStateResolved = true;
                        this.$notify({title: this.$t('Success'), text: this.$t('Template erfolgreich veröffentlicht'), type: 'success'});

                        return publishResponse;
                    });
                }

                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Entwurf gespeichert') : this.$t('Entwurf angelegt'), type: 'success'});

                return response;
            });
        },
        openPreview(previewWindow = null) {
            const languageId = this.activeTranslation ? this.activeTranslation.cms_language_id : (this.languageOptions[0] ? this.languageOptions[0].id : null);
            const payload = _.cloneDeep(this.template);

            return axios.post('api/workspace/admin/cms/templates/preview', payload, {
                params: languageId ? {language_id: languageId} : {},
            }).then((response) => {
                const previewUrl = response.data.url;

                if (previewWindow) {
                    previewWindow.location.replace(previewUrl);
                } else {
                    window.open(previewUrl, '_blank', 'noopener');
                }

                this.$notify({title: this.$t('Success'), text: this.$t('Vorschau geöffnet'), type: 'success'});

                return response;
            }).catch((error) => {
                if (previewWindow && previewWindow.location.href === 'about:blank') {
                    previewWindow.close();
                }

                throw error;
            });
        },
        openTemplateHtmlMediaDrawer() {
            this.$refs.templateHtmlEditor?.openMediaDrawer();
        },
        syncTranslations() {
            if (!Array.isArray(this.languageOptions) || this.languageOptions.length < 1) {
                this.template.translations = [];
                this.activeTranslationIndex = 0;
                return;
            }

            const existingByLanguageId = new Map(
                (Array.isArray(this.template.translations) ? this.template.translations : []).map((translation) => [
                    translation.cms_language_id,
                    translation,
                ])
            );

            this.template.translations = this.languageOptions.map((language) => {
                const existing = existingByLanguageId.get(language.id);

                return {
                    id: existing?.id || null,
                    cms_language_id: language.id,
                    layout_html: existing?.layout_html || '',
                    language: {
                        id: language.id,
                        locale: language.locale,
                        name: language.name,
                        is_default: !!language.is_default,
                    },
                };
            });

            this.activeTranslationIndex = Math.max(0, Math.min(this.activeTranslationIndex, this.template.translations.length - 1));
        },
        translationLabel(translation, index) {
            const language = translation.language || this.languageOptions.find((item) => item.id === translation.cms_language_id);
            return language ? language.locale : `${this.$t('Sprache')} ${index + 1}`;
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>

<style scoped>

.cms-translation-html-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    height: 34rem;
    min-height: 34rem;
    align-items: stretch;
}

.cms-variable-column {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    border-left: 1px solid rgb(243, 244, 246);
    background: #ffffff;
}

.cms-variable-column-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0 !important;
    overflow: hidden;
}

.cms-variable-column-body > div {
    height: 100%;
    max-height: none !important;
}

.cms-variable-row {
    padding: 0.875rem 1rem;
    border-bottom: 1px solid rgb(243, 244, 246);
}

.cms-variable-row:last-child {
    border-bottom: 0;
}

@media (max-width: 1279px) {
    .cms-translation-html-layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .cms-variable-column {
        border-left: 0;
        border-top: 1px solid rgb(243, 244, 246);
    }
}
</style>
